import { useCallback, useEffect, useRef, useState } from 'react';
import { Maximize2, Minimize2, Play, Volume2, VolumeX } from 'lucide-react';
import { extractVideoPosterFrame } from '../lib/extractVideoPosterFrame';

type Props = {
  src: string;
  /** JPEG первого кадра с API (/api/media/catalog/{id}/poster). */
  posterUrl?: string;
  show: boolean;
  onError: () => void;
  stopClickBubble?: boolean;
  /** Карусель: открыть lightbox; autoplay=true — сразу воспроизвести там */
  onExpand?: (autoplay: boolean) => void;
  /** Lightbox: автозапуск при показе */
  autoPlay?: boolean;
  /** Полноэкранный просмотр (lightbox) */
  theater?: boolean;
  /** Lightbox: свернуть обратно в карусель на карточке */
  onCollapseTheater?: () => void;
};

/**
 * Видео в карусели / lightbox: постер, Play, без звука до тапа на громкость, разворот на весь экран.
 */
export function ProductGroupHeroVideo({
  src,
  posterUrl,
  show,
  onError,
  stopClickBubble,
  onExpand,
  autoPlay = false,
  theater = false,
  onCollapseTheater,
}: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [framePoster, setFramePoster] = useState<string | undefined>();
  const [posterFailed, setPosterFailed] = useState(false);

  const displayPoster = !posterFailed && (posterUrl || framePoster);

  const playVideo = useCallback(async () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = muted;
    try {
      await v.play();
      setPlaying(true);
    } catch {
      /* autoplay policy */
    }
  }, [muted]);

  useEffect(() => {
    if (!show) {
      setPlaying(false);
      setMuted(true);
      setFramePoster(undefined);
      setPosterFailed(false);
      const v = videoRef.current;
      if (v) {
        v.pause();
        try {
          v.currentTime = 0;
        } catch {
          /* ignore */
        }
      }
    }
  }, [show]);

  useEffect(() => {
    setPlaying(false);
    setMuted(true);
    setPosterFailed(false);
    setFramePoster(undefined);
    const v = videoRef.current;
    if (v) {
      v.pause();
      try {
        v.currentTime = 0;
      } catch {
        /* ignore */
      }
    }
  }, [src]);

  useEffect(() => {
    if (!show || !autoPlay) return;
    void playVideo();
  }, [show, autoPlay, playVideo, src]);

  useEffect(() => {
    if (!show || posterUrl || framePoster || !src.trim()) return;
    let cancelled = false;
    void extractVideoPosterFrame(src).then((dataUrl) => {
      if (!cancelled && dataUrl) setFramePoster(dataUrl);
    });
    return () => {
      cancelled = true;
    };
  }, [show, src, posterUrl, framePoster]);

  useEffect(() => {
    const v = videoRef.current;
    if (v) v.muted = muted;
  }, [muted, playing]);

  const startPlay = (e: React.MouseEvent) => {
    if (stopClickBubble) e.stopPropagation();
    e.preventDefault();
    if (onExpand && !theater) {
      onExpand(true);
      return;
    }
    void playVideo();
  };

  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setMuted((m) => !m);
  };

  const handleCollapseTheater = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (theater && onCollapseTheater) {
      onCollapseTheater();
    }
  };

  const handleExpandTheater = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!theater && onExpand) {
      onExpand(true);
    }
  };

  if (!show || !src.trim()) return null;

  const chromeBtn =
    'min-w-[44px] min-h-[44px] rounded-full flex items-center justify-center touch-manipulation press';
  const chromeBtnStyle = {
    background: 'rgba(10,25,47,0.78)',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(99,179,237,0.35)',
    color: '#fff',
  } as const;

  return (
    <div className="absolute inset-0 w-full h-full z-[2]">
      <video
        ref={videoRef}
        src={src}
        poster={displayPoster || undefined}
        playsInline
        muted={muted}
        preload="metadata"
        controls={false}
        className={`w-full h-full object-contain absolute inset-0 ${playing ? 'z-[2]' : 'z-[1] opacity-0'}`}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onError={onError}
        onClick={
          playing && stopClickBubble
            ? (e) => {
                e.stopPropagation();
              }
            : undefined
        }
      />
      {displayPoster && !playing && (
        <img
          src={displayPoster}
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-full object-contain z-[3] pointer-events-none"
          referrerPolicy="no-referrer"
          onError={() => {
            if (posterUrl) setPosterFailed(true);
          }}
        />
      )}
      {!playing && onExpand && !theater && (
        <button
          type="button"
          aria-label="Открыть видео на весь экран"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onExpand(false);
          }}
          className={`absolute top-3 right-3 z-[5] ${chromeBtn}`}
          style={{
            background: 'rgba(10,25,47,0.72)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(99,179,237,0.35)',
            color: '#fff',
          }}
        >
          <Maximize2 size={20} />
        </button>
      )}
      {!playing && (
        <button
          type="button"
          aria-label="Воспроизвести видео"
          onClick={startPlay}
          className={`absolute inset-0 flex items-center justify-center touch-manipulation ${theater ? 'z-[12]' : 'z-[4]'}`}
        >
          <span
            className="w-[72px] h-[72px] rounded-full flex items-center justify-center press"
            style={{
              background: 'rgba(255,255,255,0.92)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
            }}
          >
            <Play size={32} fill="#0A192F" color="#0A192F" className="ml-1" />
          </span>
        </button>
      )}
      {playing && (
        <div
          className={`absolute right-3 z-[6] flex items-center gap-2 p-1 rounded-full ${theater ? 'bottom-20' : 'bottom-10'}`}
          style={{
            background: 'rgba(10,25,47,0.55)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(99,179,237,0.28)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
          }}
        >
          <button
            type="button"
            aria-label={muted ? 'Включить звук' : 'Выключить звук'}
            onClick={toggleMute}
            className={chromeBtn}
            style={{
              ...chromeBtnStyle,
              color: muted ? 'rgba(255,255,255,0.55)' : '#fff',
            }}
          >
            {muted ? <VolumeX size={20} /> : <Volume2 size={20} />}
          </button>
          {theater && onCollapseTheater && (
            <button
              type="button"
              aria-label="Вернуться к карточке"
              onClick={handleCollapseTheater}
              className={chromeBtn}
              style={chromeBtnStyle}
            >
              <Minimize2 size={20} />
            </button>
          )}
          {!theater && onExpand && (
            <button
              type="button"
              aria-label="Открыть видео на весь экран"
              onClick={handleExpandTheater}
              className={chromeBtn}
              style={chromeBtnStyle}
            >
              <Maximize2 size={20} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
